import type { ContentSummary } from "tds/contentSummary";
import { getCollectionItem } from "common/contentCollection";
import { formatDueDate, isOverdue } from "../helpers/time";
import * as locales from "./locales";
import { getAssetsUrl } from "common/helpers";
import { I18n } from "@devforce/trailhead-client-locale";
import salesforceLogo from "../../../../public/images/salesforce-favicon.svg";
import { QueryAdapterValue } from "common/graphqlAdapter";
import { AssignedLearningSummaryConnectionType } from "@devforce/trailhead-client-gql";

const i18n = new I18n({ assignments: locales });

export type Result = QueryAdapterValue<{
  assignedLearnings: AssignedLearningSummaryConnectionType;
}>;

export function generateDueDateHtml(assignment: ContentSummary["assignment"]) {
  if (!assignment) return "";
  const isAssignmentOverdue = assignment.dueDate
    ? !assignment.completed && isOverdue(assignment.dueDate)
    : false;
  const formattedDueDate =
    (assignment.dueDate && formatDueDate(assignment.dueDate)) || "";
  const dueDateLabel = isAssignmentOverdue
    ? i18n.t("assignments", "overdueLabel", { formattedDueDate })
    : i18n.t("assignments", "dueDateLabel", { formattedDueDate });
  return `
    <div class="due-date">
      <tds-badge${
        isAssignmentOverdue ? " variant='badge-error'" : ""
      }>${dueDateLabel}</tds-badge>
    </div>
  `;
}

export function stripEstimatedTimeLeft(
  contentSummary: ContentSummary | null
): ContentSummary | null {
  if (!contentSummary) return null;
  const { user, ...rest } = contentSummary;
  return {
    ...rest,
    user: {
      ...user,
      estimatedTimeLeft: null,
    },
  };
}

export function formatDueDateLabel(assignment: ContentSummary["assignment"]) {
  if (!assignment) return "";
  return assignment.dueDate ? formatDueDate(assignment.dueDate) : "";
}

export function generateAssignedByHtml(
  assignment: ContentSummary["assignment"]
) {
  if (!assignment) return "";
  const assignedByLabel = i18n.t("assignments", "assignedByLabel", {
    assignedBy: assignment.assignedBy,
  });
  return `
    <div class="assigned-by" style="display: flex; align-items: center; gap: var(--tds-spacing-2);">
      <img class="logo" alt="${i18n.t(
        "assignments",
        "salesforceLogoAltText"
      )}" src="${getAssetsUrl(salesforceLogo)}" />
      <span>${assignedByLabel}</span>
    </div>
  `;
}

/**
 * Normalizes user percentage complete - converts 0.0 to undefined to avoid showing 0% progress on trails that have not been started
 */
export function normalizeUserPercentageComplete(user: ContentSummary["user"]) {
  if (!user) return user;

  return user.percentageComplete === 0.0 &&
    (user.state === "NOT_STARTED" || !user.state)
    ? { ...user, percentageComplete: undefined }
    : user;
}

/**
 * Appends a collection item with assignment-specific data (HTML content and assignment object)
 */
export function appendAssignmentMetadata(
  item: ReturnType<typeof getCollectionItem>,
  assignment: ContentSummary["assignment"]
) {
  return {
    ...item,
    summary: {
      ...item.summary,
      user: normalizeUserPercentageComplete(item.summary.user),
      actionHtml: generateDueDateHtml(assignment),
      metaLeftHtml: generateAssignedByHtml(assignment),
      pretitle: i18n.t("assignments", "employeetrail_pretitle"),
      assignment,
    },
  };
}

export function getAssignmentCollectionItems(result: Result) {
  const edges = result.data.assignedLearnings.edges || [];

  return edges
    .map((edge) => edge?.node)
    .filter((node): node is NonNullable<typeof node> => node != null)
    .map((assignedLearning, index) => {
      const collectionItem = getCollectionItem(assignedLearning.summary);
      const assignmentItem = appendAssignmentMetadata(
        collectionItem,
        assignedLearning.assignment
      );

      // Create a unique key by combining summary ID with index to prevent duplicate keys
      const uniqueKey = `${assignedLearning.summary.id}:${index}`;

      return {
        ...assignmentItem,
        summary: {
          ...assignmentItem.summary,
          id: uniqueKey, // Override the ID to be unique
        },
      };
    });
}
